/*
 * Copyright (c) 2024, King Living
 * Calendly showroom override — showroom detail sets the store's Calendly ID; other routes
 * resolve the segment from saved location (profile / cookie) then nearest bookable store (FSD).
 */

import React, {createContext, useCallback, useContext, useMemo, useState} from 'react';
import PropTypes from 'prop-types';

import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';

import {CALENDLY_FIND_SHOWROOM_APP_PATH} from '@amblique/king-living/app/constants/calendly-booking-links';
import {CalendlyConsultationLinksContext} from '@amblique/king-living/app/contexts/calendly-consultation-links-context';
import {useCalendlyConfig} from '@amblique/king-living/app/hooks/use-calendly-config';
import {useCalendlyNonShowroomShowroomId} from '@amblique/king-living/app/hooks/use-calendly-non-showroom-showroom-id';
import {useCalendlyTrigger} from '@amblique/king-living/app/hooks/use-calendly-trigger';

const CalendlyShowroomOverrideContext = createContext(null);

/**
 * @returns {Object} context with setCalendlyShowroomIdOverride
 */
export const useCalendlyShowroomOverride = () => {
  const ctx = useContext(CalendlyShowroomOverrideContext);

  if (!ctx) {
    throw new Error('useCalendlyShowroomOverride must be used within CalendlyTriggerProvider');
  }

  return ctx;
};

/**
 * Mounts Calendly document-level click/hash handling app-wide so Contentful links work on
 * the home page and other routes, not only on showroom detail.
 * Showroom detail sets an override for store-specific `c_calendlyShowroomId`.
 */
export const CalendlyTriggerProvider = ({children}) => {
  const [showroomPageCalendlyId, setShowroomPageCalendlyId] = useState(undefined);
  const calendlyConfig = useCalendlyConfig();
  const {buildUrl} = useMultiSite();

  const setCalendlyShowroomIdOverride = useCallback((id) => {
    setShowroomPageCalendlyId(id);
  }, []);

  const resolveNonShowroomId = Boolean(calendlyConfig.enabled && showroomPageCalendlyId == null);
  const {calendlyShowroomId: nonShowroomCalendlyId, isResolutionComplete: nonShowroomResolutionComplete} =
    useCalendlyNonShowroomShowroomId(resolveNonShowroomId);

  const calendlyShowroomId = showroomPageCalendlyId ?? nonShowroomCalendlyId ?? undefined;

  const findShowroomHref = useMemo(
    () => buildUrl(CALENDLY_FIND_SHOWROOM_APP_PATH),
    [buildUrl]
  );

  const showFindShowroomFallback = Boolean(
    calendlyConfig.enabled &&
    resolveNonShowroomId &&
    nonShowroomResolutionComplete &&
    !calendlyShowroomId
  );

  const {inPersonUrl, videoUrl} = useCalendlyTrigger({
    enabled: calendlyConfig.enabled,
    calendlyShowroomId,
    inPersonUrlTemplate: calendlyConfig.inPersonUrlTemplate,
    videoUrlTemplate: calendlyConfig.videoUrlTemplate,
  });

  const documentConsultationCalendlyValue = useMemo(() => {
    // CL-011: active with no booking types hides #calendly-* CTAs app-wide (KINDW-1622).
    if (!calendlyConfig.enabled) {
      return {
        active: true,
        inPersonBookingAvailable: false,
        videoBookingAvailable: false,
      };
    }

    if (!calendlyShowroomId) {
      return {
        active: true,
        inPersonBookingAvailable: false,
        videoBookingAvailable: false,
        showFindShowroomFallback,
        findShowroomHref,
      };
    }

    return {
      active: true,
      inPersonBookingAvailable: Boolean(inPersonUrl),
      videoBookingAvailable: Boolean(videoUrl),
    };
  }, [
    calendlyConfig.enabled,
    calendlyShowroomId,
    inPersonUrl,
    videoUrl,
    showFindShowroomFallback,
    findShowroomHref
  ]);

  const contextValue = useMemo(
    () => ({setCalendlyShowroomIdOverride}),
    [setCalendlyShowroomIdOverride]
  );

  return (
    <CalendlyConsultationLinksContext.Provider value={documentConsultationCalendlyValue}>
      <CalendlyShowroomOverrideContext.Provider value={contextValue}>
        {children}
      </CalendlyShowroomOverrideContext.Provider>
    </CalendlyConsultationLinksContext.Provider>
  );
};

CalendlyTriggerProvider.propTypes = {
  children: PropTypes.node,
};
